import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Page } from "../../analytics";
import { Button } from "../../Button";
import BotDetection from "../../components/BotDetection";
import { LOGO_ALT, TWILIO_SENDGRID_LOGO } from "../../constants";
import { signupContext } from "../../context";
import ErrorAlert from "../../ErrorAlert";
import { ExternalLink } from "../../external-navigate";
import { useFormState } from "../../hooks/useFormState";
import { TextInput } from "../../TextInput";
import { useSessionUsername, useUnifiedAuth } from "../../unified-auth";

export const idpAuthErrorMessage = `Your login was unsuccessful, please try again. If issues persist, contact your company's Single Sign-On administrator.`;
export const usernameNotFoundErrorMessage = (
  <p style={{ margin: "0 10px" }}>
    Username not found. If you are using{" "}
    <a
      target="_blank"
      rel="noreferrer"
      href="https://www.twilio.com/docs/sendgrid/ui/account-and-settings/twilio-login-overview">
      Twilio login
    </a>
    , please use your email address as your username.
  </p>
);
export const teammateInviteUsernameNotAvailableErrorMessage = `Your email is already linked to a SendGrid account. Ask your SendGrid account administrator to be invited to this account with a different email address. Need help? Contact support.`;
export const teammateInviteEmailMismatchErrorMessage = `You accepted the invitation with a different email address. Please try again with the email address that was invited to the SendGrid account. Need help? Contact support.`;
export const teammateInviteInvalidTokenErrorMessage = `Your invitation has expired. Please ask your SendGrid account administrator to invite you again.`;
export const teammateInviteUnauthorizedErrorMessage = `Your invitation could not be accepted since you were not successfully logged in. Please try again. Need help? Contact support.`;
export const teammateInviteInternalErrorMessage = `An unexpected error occurred accepting the invitation. Please try again later. Need help? Contact support.`;
export const teammateInviteAccountLimitReachedErrorMessage = `SendGrid account limit reached.`;
export const userForbiddenAccessErrorMessage = `You are not authorized to access this account. Please contact your administrator or support for help.`;
export const teammateInviteMustLinkBeforeAcceptingErrorMessage = (email?: string) => (
  <p style={{ margin: "0 10px" }}>
    {email ? (
      <>
        To accept this invite, you must first log in to SendGrid with the username{" "}
        <strong>{email}</strong> and set up Twilio login. See{" "}
      </>
    ) : (
      <>
        To accept this invite, you must first log in using the email address from your invitation
        and connect your account with Twilio login. See{" "}
      </>
    )}
    <a
      target="_blank"
      rel="noreferrer"
      href="https://www.twilio.com/docs/sendgrid/ui/account-and-settings/twilio-login-overview#migrate-your-sendgrid-account-to-twilio-login">
      Twilio login overview
    </a>{" "}
    for more details.
  </p>
);
export const ipAddressNotAllowed = (
  <p style={{ margin: "0 10px" }}>
    Your IP address is not allowed. See{" "}
    <a
      target="_blank"
      rel="noreferrer"
      href="https://www.twilio.com/docs/sendgrid/ui/account-and-settings/ip-access-management">
      https://www.twilio.com/docs/sendgrid/ui/account-and-settings/ip-access-management
    </a>{" "}
    for more details.
  </p>
);
interface IdentifierProps {
  botDetectionEnabled: boolean;
  botDetectionSitekey: string;
  botDetectionToken?: string;
  botDetectionTimeoutMs?: number;
}

export default function Identifier({
  botDetectionEnabled,
  botDetectionSitekey,
  botDetectionToken = "",
  botDetectionTimeoutMs,
}: IdentifierProps) {
  const { navigate, isLoading, ssoLoginInfo: ssoInfo, isSSOLogin, error } = useUnifiedAuth();
  const [sessionUsername] = useSessionUsername();
  const [username, , changeUsername] = useFormState(sessionUsername);
  const [errorBanner, setErrorBanner] = useState<string | Error | JSX.Element | undefined>("");
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string>(botDetectionToken);
  const [forceReload, setForceReload] = useState<number>(0);

  const reloadBotDetection = () => {
    // Reset bot detection token
    setToken("");
    setForceReload((prev) => prev + 1);
  };
  useEffect(() => {
    if (searchParams.has("error") && searchParams.has("error_description")) {
      const errorDescription = searchParams.get("error_description") || "";
      setErrorBanner(errorDescription);
      reloadBotDetection();
    } else if (searchParams.has("error")) {
      const errorCode = searchParams.get("error") || "";
      const email = searchParams.get("email") || "";

      const errorMessages = {
        ERR_USERNAME_NOT_FOUND: usernameNotFoundErrorMessage,
        ERR_TEAMMATE_INVITE_USERNAME_NOT_AVAILABLE: teammateInviteUsernameNotAvailableErrorMessage,
        ERR_TEAMMATE_INVITE_EMAIL_MISMATCH: teammateInviteEmailMismatchErrorMessage,
        ERR_TEAMMATE_INVITE_INVALID_TOKEN: teammateInviteInvalidTokenErrorMessage,
        ERR_TEAMMATE_INVITE_UNAUTHORIZED: teammateInviteUnauthorizedErrorMessage,
        ERR_TEAMMATE_INVITE_INTERNAL: teammateInviteInternalErrorMessage,
        ERR_TEAMMATE_INVITE_ACCOUNT_LIMIT_REACHED: teammateInviteAccountLimitReachedErrorMessage,
        ERR_TEAMMATE_INVITE_MUST_LINK_BEFORE_ACCEPTING:
          teammateInviteMustLinkBeforeAcceptingErrorMessage(email),
        ERR_IP_ADDRESS_NOT_ALLOWED: ipAddressNotAllowed,
        ERR_USER_FORBIDDEN_ACCESS: userForbiddenAccessErrorMessage,
      } as const;

      const errorMessage =
        errorMessages[errorCode as keyof typeof errorMessages] || errorCode || idpAuthErrorMessage;
      setErrorBanner(errorMessage);
      reloadBotDetection();
    } else {
      if (error) {
        setErrorBanner(error);
        reloadBotDetection();
      }
    }
  }, [error, searchParams]);

  const formSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await navigate({
      identifier: sanitizeUsername(username),
      ...(token && { bot_detection_token: token }),
    });
  };

  useEffect(() => {
    if (isSSOLogin) {
      document.forms[0].submit();
    }
  }, [isSSOLogin]);

  const style = {
    marginTop: "10pt",
  };

  if (isSSOLogin) {
    return (
      <form method="post" name="frm" action={ssoInfo?.idp_url} style={{ display: "none" }}>
        <input type="hidden" name="SAMLRequest" value={ssoInfo?.saml_request} />
        <input type="hidden" name="RelayState" value={ssoInfo?.relay_state} />
        <input type="submit" />
      </form>
    );
  }
  return (
    <main className="login">
      {errorBanner && <ErrorAlert input={errorBanner} />}

      <div className="login-container">
        <div data-role="login-modals" className="login-modals">
          <div className="login-modal">
            <div className="login-modal-table">
              <div className="login-modal-table-row">
                <div className="login-modal-body">
                  <div className="login-logo">
                    <img alt={LOGO_ALT} src={TWILIO_SENDGRID_LOGO} />
                  </div>
                  <form data-role="login-form" className="login-form" onSubmit={formSubmit}>
                    <div className="row">
                      <div className="col-8 col-start-3">
                        <TextInput
                          label="Username (this may be your email address)"
                          id="username"
                          value={username}
                          autoFocus={true}
                          autoComplete="username"
                          onChange={changeUsername}
                          containerRole={"usernameContainer"}
                        />
                      </div>
                    </div>
                    <div className="row login-username-options">
                      <div className="col-10">
                        <a className="login-link" href="/login/forgot-username">
                          Forgot your username?
                        </a>
                      </div>
                    </div>
                    <div className="row" style={style}>
                      <div className="col-8 col-start-3">
                        {botDetectionEnabled && (
                          <BotDetection
                            siteKey={botDetectionSitekey}
                            onVerify={setToken}
                            onError={setErrorBanner}
                            timeoutMs={botDetectionTimeoutMs}
                            onTimeout={setToken}
                            page={Page.identifier}
                            key={`bot-detection-${forceReload}`}
                          />
                        )}
                      </div>
                    </div>
                    <div className="login-btn">
                      <Button
                        dataRole="continue-btn"
                        className="login-sso-btn"
                        isLoading={isLoading}
                        isDisabled={isLoading || (botDetectionEnabled && token.trim() === "")}>
                        Continue
                      </Button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="login-modal-table-row">
                <div className="login-modal-footer" data-role="pricing-container">
                  Don&apos;t have a Twilio SendGrid account?&nbsp;
                  <ExternalLink to={"/"} context={signupContext}>
                    Sign up now!
                  </ExternalLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

const sanitizeUsername = (username: string): string => username.trim();
